import { useEffect, useState } from "react"
import { Assessment, Course, Subject } from "../../../../types/dataModel"
import useFetch from "../../../../hooks/useFetch"
import { API } from "../../../../common/Constants"
import PreLoader from "../../../../core/common/preLoader"
import { getAPIHeaders } from "../../../../common/Utils"
import FormFooter from "../../../../core/common/formFooter"
import CommonSelectV2, { Option } from "../../../../core/common/commonSelectV2"

interface Props {
    assessment?: Assessment
    onSuccess: () => void
}

// New interface to track course-subject relationships
interface CourseWithSubject {
    courseId: number;
    assessmentId: number;
    subjectId?: number;
    course: Course;
    subject?: Subject;
}

const customComponents = {
  Option: (props: any) => (
    <div {...props.innerProps} ref={props.innerRef} style={props.isFocused ? { backgroundColor: '#f0f0f0' } : {}}>
      <div className="p-2">
        <h6>
          {props.data.label}
          <span className={`ms-2 badge rounded-pill bg-light`}>
            {props.data.value}
          </span>
        </h6>
      </div>
    </div>
  ),
  // You can add more custom components here if needed
};

const formatOptionLabel = (option: any) => (
  <div className="pt-1">
    <h6 className="text-primary">
      {option.label}
      <span className={`ms-2 badge rounded-pill bg-primary`}>
        {option.value}
      </span>
    </h6>
  </div>
);

const AssessmentCourses = ({ assessment, onSuccess }: Props) => {

    const [formData, setFormData] = useState<Assessment | undefined>(assessment)
    const [msgBox, setMsgBox] = useState<any>({
        msg: "",
        error: false
    });
    const [courses, setCourses] = useState<Course[]>();
    const [selectedCoursesWithSubjects, setSelectedCoursesWithSubjects] = useState<CourseWithSubject[]>([]);
    const { isLoading, error, callFetch } = useFetch();

    useEffect(() => {
        fetchCourses()
    },[])

    useEffect(() => {
        setFormData(assessment)
        
        // Initialize course-subject relationships if assessment has courses
        if (assessment?.courses?.length && courses?.length) {
            const initialSelections = assessment.courses.map(course => {
                return {
                    courseId: course?.id!,
                    assessmentId: assessment?.id!,
                    course: course,
                    subjectId: course?.subjects?.[0]?.id || undefined,
                    subject: course?.subjects?.[0],
                };
            });
            setSelectedCoursesWithSubjects(initialSelections);
        }
    }, [assessment, courses])

    const reset = () => {
        resetMsgBox()
    }

    const resetMsgBox = () => {
        setMsgBox({
            msg: "",
            error: false
        })
    }

    const fetchCourses = () => {
        callFetch(`${API.COURSE}`,
            'GET',
            getAPIHeaders(),
            null,
            null
        ).then((res) => {
            if (res) {
                setCourses(res)
            }
        });
    }

    // Function to handle subject selection for a course
    const handleSubjectChange = (courseId: number, subjectValue: string) => {
        // Find the course object
        const course = courses?.find(c => c?.id === courseId);
        if (!course) return;

        // Find the subject object within the course
        const subject = subjectValue ? 
            course.subjects?.find(s => s.code === subjectValue) : 
            undefined;

        // Update the selectedCoursesWithSubjects array
        const updatedItems = [...selectedCoursesWithSubjects];
        const courseIndex = updatedItems.findIndex(item => item.courseId === courseId);
        
        if (courseIndex >= 0) {
            // Update existing entry
            updatedItems[courseIndex].subjectId = subject?.id;
            updatedItems[courseIndex].subject = subject;
        } else {
            // Create new entry
            updatedItems.push({
                courseId,
                assessmentId: formData?.id || 0,
                course: course,
                subjectId: subject?.id,
                subject
            });
        }
        
        setSelectedCoursesWithSubjects(updatedItems);
    }

    // Function to get the currently selected subject for a course
    const getSelectedSubjectForCourse = (courseId: number): Option | null => {
        const item = selectedCoursesWithSubjects.find(item => item.courseId === courseId);
        if (item?.subject) {
            return {
                value: item.subject.code || '',
                label: item.subject.title || ''
            };
        }
        return null;
    }

    // Helper function to parse subjects to dropdown options
    const parseSubjectsToOptions = (subjects?: Subject[]): Option[] => {
        if (subjects && subjects.length > 0) {
            return subjects.map((subject) => {
                return {
                    value: subject.code || '',
                    label: subject.title || ''
                };
            });
        }
        return [];
    }

    useEffect(() => {
        if (error) {
            if (error.response?.data) {
                setMsgBox({ msg: error.response?.data, error: true })
            } else {
                setMsgBox({ msg: 'Something went wrong please try again later.', error: true })
            }
        }
    }, [error]);

    const handleSubmit = (event: any) => {
        event.preventDefault()
        if (!formData?.courses?.length) {
            setMsgBox({ msg: "Please select at least one course.", error: true })
        } else {
            setMsgBox({ msg: "", error: false })
            
            // Create payload with course-subject relationships
            const payload = formData.courses.map((cr: Course) => {
                const selectedItem = selectedCoursesWithSubjects.find(item => item.courseId === cr.id);
                return {
                    assessmentId: formData.id,
                    courseId: cr.id,
                    subjectId: selectedItem?.subjectId || null // Include subject ID if selected
                };
            });
            
            callFetch(API.ADD_ASSESSMENT_COURSES,
                'POST',
                getAPIHeaders(),
                null,
                payload
            ).then(() => {
                setMsgBox({ msg: "Updated successfully", error: false })
                onSuccess()
            });
        }
    }

    return (
        <div className="modal-dialog modal-dialog-centered modal-md">
            <PreLoader showLoader={isLoading} />
            <div className="modal-content">
                <div className="modal-header">
                    <h4 className="modal-title">Assessment: {formData?.title}</h4>
                    <button
                        type="button"
                        className="btn-close custom-btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                        onClick={reset}
                    >
                        <i className="ti ti-x" />
                    </button>
                </div>
                <form>
                    <div className="modal-body pb-0">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="mb-3">
                                    <label className="form-label">Select Courses</label>
                                    <CommonSelectV2
                                        isMulti
                                        onChange={(value:any[])=>{
                                            let newCrs:any[] = value?.map((v:string)=>{
                                                return courses?.find((cr:Course) => cr?.code === v)
                                            })
                                            setFormData({
                                                ...formData,
                                                courses: [...newCrs]
                                            })

                                            const updatedSelectedItems = selectedCoursesWithSubjects.filter(item => 
                                                newCrs.some(c => c?.id === item.courseId)
                                            );
                                            
                                            newCrs.forEach(course => {
                                                if (!updatedSelectedItems.some(item => item.courseId === course?.id)) {
                                                    updatedSelectedItems.push({
                                                        courseId: course?.id,
                                                        assessmentId: formData?.id || 0,
                                                        course: course,
                                                        subjectId: undefined,
                                                        subject: undefined
                                                    });
                                                }
                                            });
                                            
                                            setSelectedCoursesWithSubjects(updatedSelectedItems);
                                        }}
                                        className="select"
                                        values={formData?.courses?.length ? parseToOptions(formData?.courses) : null}
                                        defaultValues={parseToOptions(formData?.courses)}
                                        options={parseToOptions(courses)}
                                        disabled={formData?.isLive ? true : false}
                                        components={customComponents}
                                        formatOptionLabel={formatOptionLabel}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Subject selection for each course */}
                        {formData?.courses && formData.courses.length > 0 && (
                            <div className="row">
                                <div className="col-md-12">
                                    <h6 className="mb-3">Select Subjects (Optional)</h6>
                                    <div style={{ 
                                        maxHeight: '300px', 
                                        overflowY: 'auto', 
                                        padding: '5px',
                                        border: '1px solid #e9e9e9',
                                        borderRadius: '4px'
                                    }}>
                                        {formData.courses.map((course: Course) => (
                                            <div key={course?.id} className="card mb-1">
                                                <div className="card-body">
                                                    <label className="form-label">{course.title}</label>
                                                    <div>
                                                        <CommonSelectV2
                                                            onChange={(value: string) => handleSubjectChange(course?.id!, value)}
                                                            className="select"
                                                            options={parseSubjectsToOptions(courses?.find(c => c?.id === course?.id)?.subjects)}
                                                            value={getSelectedSubjectForCourse(course?.id!)}
                                                            placeHolder="Select Subject (Optional)"
                                                            disabled={formData?.isLive ? true : false}
                                                            components={customComponents}
                                                            formatOptionLabel={formatOptionLabel}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <FormFooter
                            onCancel={reset}
                            onSave={handleSubmit}
                            error={msgBox.error}
                            message={msgBox.msg}
                        />
                    </div>
                </form>
            </div>
        </div>
    )

}

function parseToOptions (courses?:Course[]): any[] {
    if(courses){
        return courses?.map((item)=> { 
            return {  
                value: item?.code,
                label: item?.title
            }
        })
    } else {
        return []
    }
}

export default AssessmentCourses